/**
 * VCardPublic.js  — Premium multi-template vCard renderer
 *
 * 8 distinct templates, each with its own layout:
 *   modern | musician | minimal | bold | luxury | creative | nature | corporate
 *
 * Each template respects:
 *   theme_color, font_family, bg_type, bg_value, bg_overlay
 *
 * Existing JJuma logic (API call, SEO meta, contact download, share) is preserved exactly.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getBackendUrl } from '../../lib/env';
import { getPayUrl } from '../../lib/domains';

const API_URL = getBackendUrl();

/* ─── config ──────────────────────────────────────────────── */
export const FONT_MAP = {
  outfit:     "'Outfit', sans-serif",
  poppins:    "'Poppins', sans-serif",
  inter:      "'Inter', sans-serif",
  playfair:   "'Playfair Display', serif",
  montserrat: "'Montserrat', sans-serif",
  raleway:    "'Raleway', sans-serif",
  lora:       "'Lora', serif",
  dancing:    "'Dancing Script', cursive",
  manrope:    "'Manrope', sans-serif",
};

export const THEME_ACCENT = {
  green:  '#047857',
  blue:   '#1d4ed8',
  purple: '#7c3aed',
  gold:   '#b45309',
  dark:   '#1e293b',
  rose:   '#be185d',
  teal:   '#0d9488',
  orange: '#ea580c',
};

/* ─── helpers ─────────────────────────────────────────────── */
function normalizeLinks(links) {
  if (!Array.isArray(links)) return [];
  return links.filter(l => l && l.is_active !== false)
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
}
function buildContactUrl(type, value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (type === 'phone')    return `tel:${raw.replace(/\s+/g, '')}`;
  if (type === 'whatsapp') return `https://wa.me/${raw.replace(/[^0-9]/g, '')}`;
  if (type === 'email')    return `mailto:${raw}`;
  return raw;
}
function ensureMeta(name, content, attr = 'name') {
  if (!content) return;
  const sel = attr === 'property' ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let tag = document.querySelector(sel);
  if (!tag) { tag = document.createElement('meta'); tag.setAttribute(attr, name); document.head.appendChild(tag); }
  tag.setAttribute('content', content);
}
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

/* ─── SVG social icons ────────────────────────────────────── */
const SI = {
  Facebook:  s => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>,
  Instagram: s => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>,
  TikTok:    s => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 100 12.68 6.34 6.34 0 006.33-6.34V8.75a8.22 8.22 0 004.79 1.52V6.82a4.85 4.85 0 01-1.02-.13z"/></svg>,
  YouTube:   s => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white"/></svg>,
  X:         s => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  LinkedIn:  s => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>,
};
const SOCIAL_BRAND = {
  Facebook:  '#1877F2', Instagram: '#E1306C', TikTok: '#010101',
  YouTube:   '#FF0000', X:         '#000000', LinkedIn: '#0077B5',
};
const WAIcon   = ({ s = 18 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>;
const PhoneIcon  = ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.93 10.5 19.79 19.79 0 01.86 1.36 2 2 0 012.83 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.09 7.91a16 16 0 006 6l.91-.91a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 15.28z"/></svg>;
const MailIcon   = ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const GlobeIcon  = ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>;
const MapPinIcon = ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const ShareIcon  = ({ s = 16 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>;
const CopyIcon   = ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>;
const DlIcon     = ({ s = 14 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const ExtIcon    = ({ s = 13 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
const HeartIcon  = ({ s = 15 }) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>;

/* ─── reusable sub-components ─────────────────────────────── */
function SocialRow({ social, v, size = 18, variant = 'pill' }) {
  const items = [
    ['Facebook', social.facebook || v?.facebook],
    ['Instagram', social.instagram || v?.instagram],
    ['TikTok', social.tiktok || v?.tiktok],
    ['YouTube', social.youtube || v?.youtube],
    ['X', social.x || v?.x_twitter],
    ['LinkedIn', social.linkedin || v?.linkedin],
  ].filter(([, u]) => u);
  if (!items.length) return null;
  if (variant === 'circle') return (
    <div className="flex flex-wrap justify-center gap-3">
      {items.map(([label, url]) => {
        const Icon = SI[label]; const c = SOCIAL_BRAND[label];
        return (
          <a key={label} href={url} target="_blank" rel="noreferrer" title={label}
             className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-md hover:scale-110 transition-transform"
             style={{ background: c }}>
            {Icon && Icon(size)}
          </a>
        );
      })}
    </div>
  );
  return (
    <div className="flex flex-wrap justify-center gap-2">
      {items.map(([label, url]) => {
        const Icon = SI[label]; const c = SOCIAL_BRAND[label];
        return (
          <a key={label} href={url} target="_blank" rel="noreferrer"
             className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-sm hover:opacity-90 transition-opacity"
             style={{ background: c }}>
            {Icon && Icon(14)} {label}
          </a>
        );
      })}
    </div>
  );
}

function CustomLinks({ links, accentColor, cardBg = '#f8fafc', textColor = '#1e293b' }) {
  if (!links.length) return null;
  return (
    <div className="space-y-2">
      {links.map(link => (
        <a key={link.id || link.url} href={link.url} target="_blank" rel="noreferrer"
           className="flex items-center justify-between rounded-2xl px-4 py-3 hover:opacity-90 transition-opacity"
           style={{ background: cardBg }}>
          <div className="flex items-center gap-2.5 min-w-0">
            {link.icon && <span className="text-lg">{link.icon}</span>}
            <p className="text-sm font-semibold truncate" style={{ color: textColor }}>{link.title}</p>
          </div>
          <ExtIcon s={13} />
        </a>
      ))}
    </div>
  );
}

function ShareBar({ publicUrl, copied, onCopy, onShare, accentColor, textColor = '#fff' }) {
  return (
    <div className="rounded-2xl px-4 py-3 flex items-center justify-between gap-3 bg-white/15 backdrop-blur-sm border border-white/20">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70" style={{ color: textColor }}>Share Profile</p>
        <p className="text-xs truncate opacity-90" style={{ color: textColor }}>{publicUrl}</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <button onClick={onCopy}
          className="flex items-center gap-1 rounded-xl border border-white/30 bg-white/20 px-3 py-1.5 text-xs font-bold text-white hover:bg-white/30 transition-colors">
          <CopyIcon s={12} /> {copied ? '✓' : 'Copy'}
        </button>
        <button onClick={onShare}
          className="flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:opacity-90 transition-opacity"
          style={{ background: accentColor }}>
          <ShareIcon s={12} /> Share
        </button>
      </div>
    </div>
  );
}

function BottomActions({ downloadEnabled, onDownload, onShare, accentColor }) {
  return (
    <div className="flex gap-2 mt-1">
      {downloadEnabled && (
        <button onClick={onDownload}
          className="flex-1 flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white/80 py-2.5 text-sm font-bold text-slate-700 hover:bg-white transition-colors">
          <DlIcon s={14} /> Save Contact
        </button>
      )}
      <button onClick={onShare}
        className="flex-1 flex items-center justify-center gap-2 rounded-2xl py-2.5 text-sm font-bold text-white shadow-sm hover:opacity-90 transition-opacity"
        style={{ background: accentColor }}>
        <ShareIcon s={14} /> Share
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TEMPLATE 1: MODERN  (gradient cover, clean card)
══════════════════════════════════════════════════════════════ */
function TplModern({ v, cfg, actions, links }) {
  const { accent, font, bg, publicUrl, copied } = cfg;
  const { onCopy, onShare, onDownload } = actions;
  const social = v.social_links || {};
  const whRef  = buildContactUrl('whatsapp', v.whatsapp || v.phone);
  const callRef= buildContactUrl('phone',    v.phone || v.whatsapp);
  const mailRef= buildContactUrl('email',    v.email);
  return (
    <div className="min-h-screen py-6 px-4" style={{ background: bg, fontFamily: font }}>
      <div className="mx-auto max-w-sm space-y-3">
        <ShareBar publicUrl={publicUrl} copied={copied} onCopy={onCopy} onShare={onShare} accentColor={accent} />
        <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
          {/* Cover */}
          <div className="h-40 relative" style={v.cover_image ? undefined : { background: `linear-gradient(135deg, ${accent}, ${accent}99)` }}>
            {v.cover_image && <img src={v.cover_image} alt="" className="h-full w-full object-cover" />}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.25))' }} />
          </div>
          {/* Avatar */}
          <div className="px-6 pb-6">
            <div className="relative -mt-12 mb-3 w-24 h-24 rounded-2xl overflow-hidden border-4 border-white shadow-xl bg-white">
              {v.profile_image
                ? <img src={v.profile_image} alt="" className="h-full w-full object-cover" />
                : <div className="h-full w-full flex items-center justify-center text-4xl font-bold" style={{ color: accent, background: `${accent}18` }}>{(v.display_name||v.full_name||'J').charAt(0).toUpperCase()}</div>}
            </div>
            <h1 className="text-2xl font-bold text-slate-900">{v.display_name || v.full_name}</h1>
            {(v.brand_name || v.job_title) && <p className="text-sm font-semibold mt-0.5" style={{ color: accent }}>{v.job_title || v.brand_name}</p>}
            {v.location && <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPinIcon s={11} />{v.location}</p>}
            {v.bio && <p className="text-sm text-slate-600 mt-2.5 leading-relaxed">{v.bio}</p>}
            {/* Actions */}
            <div className="mt-4 grid grid-cols-2 gap-2">
              {whRef   && <a href={whRef}   className="flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white shadow-sm" style={{ background: '#25D366' }}><WAIcon s={15} />WhatsApp</a>}
              {callRef && <a href={callRef} className="flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white shadow-sm" style={{ background: accent }}><PhoneIcon s={14} />Call</a>}
              {mailRef && <a href={mailRef} className="flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white shadow-sm bg-red-500"><MailIcon s={14} />Email</a>}
              {v.website && <a href={v.website} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 py-2.5 text-sm font-bold text-slate-700"><GlobeIcon s={14} />Website</a>}
            </div>
            {v.payment_link_id && <a href={buildContactUrl('', v.payment_link_id)} target="_blank" rel="noreferrer" className="mt-2 flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-white w-full shadow-md" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}><HeartIcon s={15} />Pay / Donate</a>}
            <div className="mt-4"><SocialRow social={social} v={v} size={16} variant="circle" /></div>
            <div className="mt-4"><CustomLinks links={links} accentColor={accent} /></div>
            <div className="mt-4"><BottomActions downloadEnabled={v.download_contact_enabled} onDownload={onDownload} onShare={onShare} accentColor={accent} /></div>
          </div>
        </div>
        {v.show_jjuma_branding !== false && <p className="text-center text-xs text-white/60">Powered by <span className="font-bold text-white">JJuma Pay</span></p>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TEMPLATE 2: MUSICIAN  (dark full-bleed, neon glows, concert vibe)
══════════════════════════════════════════════════════════════ */
function TplMusician({ v, cfg, actions, links }) {
  const { accent, font, publicUrl, copied } = cfg;
  const { onCopy, onShare, onDownload } = actions;
  const social = v.social_links || {};
  const neon   = accent;
  return (
    <div className="min-h-screen relative" style={{ fontFamily: font, background: '#0a0a0f' }}>
      {/* Full bleed background */}
      <div className="absolute inset-0 z-0">
        {v.cover_image
          ? <img src={v.cover_image} alt="" className="w-full h-64 object-cover opacity-50" />
          : <div className="w-full h-64" style={{ background: `linear-gradient(135deg, ${neon}66, #0a0a0f)` }} />}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#0a0a0f]" />
      </div>
      {/* Content */}
      <div className="relative z-10 max-w-sm mx-auto px-4 py-6 space-y-4">
        {/* Name hero */}
        <div className="pt-10 pb-4 text-center">
          <div className="w-28 h-28 mx-auto rounded-2xl overflow-hidden border-2 shadow-2xl mb-4" style={{ borderColor: neon, boxShadow: `0 0 30px ${neon}60` }}>
            {v.profile_image
              ? <img src={v.profile_image} alt="" className="h-full w-full object-cover" />
              : <div className="h-full w-full flex items-center justify-center text-4xl font-bold text-white" style={{ background: `${neon}30` }}>{(v.display_name||v.full_name||'J').charAt(0)}</div>}
          </div>
          <h1 className="text-3xl font-bold text-white" style={{ textShadow: `0 0 20px ${neon}80` }}>{v.display_name || v.full_name}</h1>
          {(v.brand_name || v.job_title) && <p className="text-sm font-semibold mt-1" style={{ color: neon }}>{v.job_title || v.brand_name}</p>}
          {v.bio && <p className="text-sm text-white/70 mt-2 leading-relaxed">{v.bio}</p>}
        </div>
        {/* Social circles */}
        <div className="flex justify-center gap-3 flex-wrap">
          {[['Facebook', social.facebook||v?.facebook],['Instagram',social.instagram||v?.instagram],['TikTok',social.tiktok||v?.tiktok],['YouTube',social.youtube||v?.youtube],['X',social.x||v?.x_twitter],['LinkedIn',social.linkedin||v?.linkedin]]
            .filter(([,u])=>u).map(([label, url]) => {
              const Icon = SI[label]; const c = SOCIAL_BRAND[label];
              return <a key={label} href={url} target="_blank" rel="noreferrer" title={label}
                className="w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform border border-white/20"
                style={{ background: c, boxShadow: `0 4px 20px ${c}60` }}>{Icon && Icon(20)}</a>;
            })}
        </div>
        {/* Contact row */}
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-4 space-y-2.5">
          {[
            [buildContactUrl('whatsapp', v.whatsapp||v.phone), <WAIcon s={15}/>, v.whatsapp||v.phone, '#25D366'],
            [buildContactUrl('phone', v.phone||v.whatsapp), <PhoneIcon s={14}/>, v.phone||v.whatsapp, neon],
            [buildContactUrl('email', v.email), <MailIcon s={14}/>, v.email, '#ef4444'],
            [v.website, <GlobeIcon s={14}/>, v.website, '#64748b'],
          ].filter(([u,,val])=>u&&val).map(([url, icon, label, color]) => (
            <a key={label} href={url} target={url?.startsWith('http')?'_blank':undefined} rel="noreferrer"
               className="flex items-center gap-3 text-sm text-white/80 hover:text-white transition-colors">
              <span className="w-8 h-8 rounded-xl flex items-center justify-center text-white" style={{ background: `${color}30`, color }}>{icon}</span>
              {label}
            </a>
          ))}
          {v.location && <div className="flex items-center gap-3 text-sm text-white/60"><span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${neon}20`, color: neon }}><MapPinIcon s={13}/></span>{v.location}</div>}
        </div>
        {/* CTA */}
        {v.payment_link_id && <a href={v.payment_link_id} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold text-white w-full shadow-lg" style={{ background: `linear-gradient(90deg, ${neon}, ${neon}bb)`, boxShadow: `0 8px 24px ${neon}40` }}><HeartIcon s={15}/>Pay / Support</a>}
        {/* Links */}
        <CustomLinks links={links} accentColor={neon} cardBg="rgba(255,255,255,0.07)" textColor="#fff" />
        {/* Share */}
        <div className="rounded-2xl border border-white/10 bg-white/5 p-3 flex items-center justify-between gap-2">
          <p className="text-xs text-white/50 truncate">{publicUrl}</p>
          <div className="flex gap-2">
            <button onClick={onCopy} className="text-xs font-bold px-3 py-1.5 rounded-xl border border-white/20 text-white/70 hover:text-white">{copied?'Copied':'Copy'}</button>
            <button onClick={onShare} className="text-xs font-bold px-3 py-1.5 rounded-xl text-white" style={{ background: neon }}>Share</button>
          </div>
        </div>
        {v.download_contact_enabled && <button onClick={onDownload} className="w-full flex items-center justify-center gap-2 rounded-2xl border border-white/20 py-2.5 text-sm font-bold text-white/70 hover:text-white"><DlIcon s={14}/>Save Contact</button>}
        {v.show_jjuma_branding !== false && <p className="text-center text-xs text-white/30">Powered by JJuma Pay</p>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TEMPLATE 3: MINIMAL  (ultra clean, left-aligned, typography-first)
══════════════════════════════════════════════════════════════ */
function TplMinimal({ v, cfg, actions, links }) {
  const { accent, font, bg, publicUrl, copied } = cfg;
  const { onCopy, onShare, onDownload } = actions;
  const social = v.social_links || {};
  return (
    <div className="min-h-screen py-8 px-4" style={{ background: bg || '#f8f9fa', fontFamily: font }}>
      <div className="mx-auto max-w-sm space-y-4">
        {/* Top thin accent bar */}
        <div className="h-1 rounded-full" style={{ background: accent }} />
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden p-7">
          {/* Left-aligned header */}
          <div className="flex items-start gap-4 mb-5">
            <div className="w-20 h-20 rounded-2xl overflow-hidden shrink-0 border border-slate-100 shadow-sm">
              {v.profile_image
                ? <img src={v.profile_image} alt="" className="h-full w-full object-cover" />
                : <div className="h-full w-full flex items-center justify-center text-3xl font-bold" style={{ color: accent, background: `${accent}10` }}>{(v.display_name||v.full_name||'J').charAt(0)}</div>}
            </div>
            <div className="min-w-0 pt-1">
              <h1 className="text-xl font-bold text-slate-900 leading-tight">{v.display_name || v.full_name}</h1>
              {(v.brand_name || v.job_title) && <p className="text-xs font-bold uppercase tracking-wide mt-0.5" style={{ color: accent }}>{v.job_title || v.brand_name}</p>}
              {v.location && <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><MapPinIcon s={10}/>{v.location}</p>}
            </div>
          </div>
          {v.bio && <p className="text-sm text-slate-600 leading-relaxed mb-5 pb-5 border-b border-slate-50">{v.bio}</p>}
          {/* Contact list */}
          <div className="space-y-2.5 mb-5">
            {[
              [buildContactUrl('whatsapp', v.whatsapp||v.phone), <WAIcon s={14}/>, v.whatsapp||v.phone, '#25D366', 'WhatsApp'],
              [buildContactUrl('phone', v.phone||v.whatsapp), <PhoneIcon s={13}/>, v.phone||v.whatsapp, accent, 'Phone'],
              [buildContactUrl('email', v.email), <MailIcon s={13}/>, v.email, '#ef4444', 'Email'],
              [v.website, <GlobeIcon s={13}/>, v.website, '#64748b', 'Website'],
            ].filter(([u,,val])=>u&&val).map(([url, icon, label, color, type]) => (
              <a key={type} href={url} target={url?.startsWith('http')?'_blank':undefined} rel="noreferrer"
                 className="flex items-center gap-3 group">
                <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}15`, color }}>{icon}</span>
                <span className="text-sm text-slate-700 group-hover:text-slate-900 transition-colors">{label}</span>
              </a>
            ))}
            {v.location && <div className="flex items-center gap-3"><span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}15`, color: accent }}><MapPinIcon s={13}/></span><span className="text-sm text-slate-700">{v.location}</span></div>}
          </div>
          {/* Social */}
          <div className="flex flex-wrap gap-2 mb-5">
            {[['Facebook',social.facebook||v?.facebook],['Instagram',social.instagram||v?.instagram],['TikTok',social.tiktok||v?.tiktok],['YouTube',social.youtube||v?.youtube],['X',social.x||v?.x_twitter],['LinkedIn',social.linkedin||v?.linkedin]]
              .filter(([,u])=>u).map(([label, url]) => {
                const Icon = SI[label]; const c = SOCIAL_BRAND[label];
                return <a key={label} href={url} target="_blank" rel="noreferrer"
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white hover:scale-105 transition-transform"
                  style={{ background: c }}>{Icon && Icon(15)}</a>;
              })}
          </div>
          {/* CTA */}
          {v.payment_link_id && <a href={v.payment_link_id} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-2xl py-2.5 text-sm font-bold text-white w-full mb-4" style={{ background: accent }}><HeartIcon s={14}/>Pay / Donate</a>}
          {/* Links */}
          <CustomLinks links={links} accentColor={accent} cardBg="#f8fafc" />
          {/* Footer */}
          <div className="mt-5 pt-4 border-t border-slate-50 flex items-center justify-between gap-2">
            <p className="text-xs text-slate-400 truncate">{publicUrl}</p>
            <div className="flex gap-1.5 shrink-0">
              {v.download_contact_enabled && <button onClick={onDownload} className="text-xs font-bold px-2 py-1 rounded-lg border border-slate-200 text-slate-600"><DlIcon s={12}/></button>}
              <button onClick={onCopy} className="text-xs font-bold px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600">{copied?'✓':'Copy'}</button>
              <button onClick={onShare} className="text-xs font-bold px-2.5 py-1 rounded-lg text-white" style={{ background: accent }}>Share</button>
            </div>
          </div>
        </div>
        {v.show_jjuma_branding !== false && <p className="text-center text-xs text-slate-400">Powered by <span className="font-bold" style={{ color: accent }}>JJuma Pay</span></p>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TEMPLATE 4: BOLD  (solid color top, white bottom, strong typography)
══════════════════════════════════════════════════════════════ */
function TplBold({ v, cfg, actions, links }) {
  const { accent, font, publicUrl, copied } = cfg;
  const { onCopy, onShare, onDownload } = actions;
  const social = v.social_links || {};
  return (
    <div className="min-h-screen" style={{ fontFamily: font }}>
      {/* Bold color top section */}
      <div className="relative pt-14 pb-20 px-6 text-center" style={{ background: accent }}>
        {v.cover_image && <div className="absolute inset-0 overflow-hidden opacity-20"><img src={v.cover_image} alt="" className="w-full h-full object-cover"/></div>}
        <div className="relative z-10">
          <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-2xl mx-auto mb-4">
            {v.profile_image
              ? <img src={v.profile_image} alt="" className="h-full w-full object-cover"/>
              : <div className="h-full w-full flex items-center justify-center text-4xl font-black text-white" style={{ background: 'rgba(255,255,255,0.2)' }}>{(v.display_name||v.full_name||'J').charAt(0)}</div>}
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight">{v.display_name || v.full_name}</h1>
          {(v.brand_name || v.job_title) && <p className="text-sm font-bold text-white/80 mt-0.5 uppercase tracking-widest">{v.job_title || v.brand_name}</p>}
        </div>
        {/* Social icons row */}
        <div className="relative z-10 flex justify-center gap-3 mt-4 flex-wrap">
          {[['Facebook',social.facebook||v?.facebook],['Instagram',social.instagram||v?.instagram],['TikTok',social.tiktok||v?.tiktok],['YouTube',social.youtube||v?.youtube],['X',social.x||v?.x_twitter],['LinkedIn',social.linkedin||v?.linkedin]]
            .filter(([,u])=>u).map(([label, url]) => {
              const Icon = SI[label];
              return <a key={label} href={url} target="_blank" rel="noreferrer" title={label}
                className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white border border-white/30 transition-colors">{Icon && Icon(16)}</a>;
            })}
        </div>
      </div>
      {/* White card overlapping */}
      <div className="mx-4 -mt-8 relative z-10 bg-white rounded-3xl shadow-2xl pb-6">
        {/* Bio + location */}
        {(v.bio || v.location) && (
          <div className="px-6 pt-6 pb-4 border-b border-slate-50">
            {v.location && <p className="text-xs text-slate-500 flex items-center gap-1 mb-2"><MapPinIcon s={11}/>{v.location}</p>}
            {v.bio && <p className="text-sm text-slate-600 leading-relaxed">{v.bio}</p>}
          </div>
        )}
        {/* Contact */}
        <div className="px-6 pt-4 space-y-2">
          {[
            [buildContactUrl('whatsapp', v.whatsapp||v.phone), <WAIcon s={15}/>, v.whatsapp||v.phone, '#25D366'],
            [buildContactUrl('phone', v.phone||v.whatsapp), <PhoneIcon s={14}/>, v.phone||v.whatsapp, accent],
            [buildContactUrl('email', v.email), <MailIcon s={14}/>, v.email, '#ef4444'],
            [v.website, <GlobeIcon s={14}/>, v.website, '#64748b'],
          ].filter(([u,,val])=>u&&val).map(([url, icon, label, color]) => (
            <a key={label} href={url} target={url?.startsWith('http')?'_blank':undefined} rel="noreferrer"
               className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-slate-50 transition-colors">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-white" style={{ background: color }}>{icon}</span>
              <span className="text-sm font-semibold text-slate-800 truncate">{label}</span>
            </a>
          ))}
        </div>
        {/* CTA */}
        {v.payment_link_id && <div className="px-6 mt-3"><a href={v.payment_link_id} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-black text-white w-full" style={{ background: accent }}><HeartIcon s={15}/>Pay / Donate</a></div>}
        {/* Links */}
        {links.length > 0 && <div className="px-6 mt-4"><CustomLinks links={links} accentColor={accent} cardBg="#f8fafc"/></div>}
        {/* Footer bar */}
        <div className="mx-6 mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
          <p className="text-xs text-slate-400 truncate">{publicUrl}</p>
          <div className="flex gap-1.5 shrink-0">
            {v.download_contact_enabled && <button onClick={onDownload} className="text-xs font-bold px-2.5 py-1.5 rounded-xl border border-slate-200 text-slate-500 flex items-center gap-1"><DlIcon s={11}/></button>}
            <button onClick={onCopy} className="text-xs font-bold px-2.5 py-1.5 rounded-xl border border-slate-200 text-slate-600">{copied?'✓ Copied':'Copy'}</button>
            <button onClick={onShare} className="text-xs font-bold px-3 py-1.5 rounded-xl text-white" style={{ background: accent }}>Share</button>
          </div>
        </div>
      </div>
      <div className="h-8" />
      {v.show_jjuma_branding !== false && <p className="text-center text-xs text-slate-400 pb-4">Powered by <span className="font-bold" style={{ color: accent }}>JJuma Pay</span></p>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TEMPLATE 5: LUXURY  (black/gold, serif, premium feel)
══════════════════════════════════════════════════════════════ */
function TplLuxury({ v, cfg, actions, links }) {
  const { accent, font, publicUrl, copied } = cfg;
  const { onCopy, onShare, onDownload } = actions;
  const gold = accent === '#047857' ? '#c9a227' : accent;
  const social = v.social_links || {};
  return (
    <div className="min-h-screen" style={{ background: '#0d0d0d', fontFamily: font, color: '#e8e0d0' }}>
      <div className="max-w-sm mx-auto px-4 py-8 space-y-5">
        {/* Gold divider line */}
        <div className="flex items-center gap-3"><div className="flex-1 h-px" style={{ background: `linear-gradient(to right, transparent, ${gold})` }}/><span className="text-xs tracking-[0.4em] uppercase" style={{ color: gold }}>Profile</span><div className="flex-1 h-px" style={{ background: `linear-gradient(to left, transparent, ${gold})` }}/></div>
        {/* Profile center */}
        <div className="text-center py-4">
          {v.cover_image && <div className="h-36 rounded-2xl overflow-hidden mb-6 relative"><img src={v.cover_image} alt="" className="w-full h-full object-cover opacity-50"/><div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent, #0d0d0d)' }}/></div>}
          <div className="w-28 h-28 mx-auto rounded-2xl overflow-hidden border-2 shadow-2xl mb-4" style={{ borderColor: gold }}>
            {v.profile_image
              ? <img src={v.profile_image} alt="" className="h-full w-full object-cover"/>
              : <div className="h-full w-full flex items-center justify-center text-4xl font-bold" style={{ color: gold, background: `${gold}15` }}>{(v.display_name||v.full_name||'J').charAt(0)}</div>}
          </div>
          <h1 className="text-2xl font-bold" style={{ color: '#f5f0e8', textShadow: `0 0 40px ${gold}30` }}>{v.display_name || v.full_name}</h1>
          {(v.brand_name || v.job_title) && <p className="text-xs tracking-[0.3em] uppercase mt-1" style={{ color: gold }}>{v.job_title || v.brand_name}</p>}
          {v.location && <p className="text-xs mt-2 flex items-center justify-center gap-1" style={{ color: '#8a7d6a' }}><MapPinIcon s={11}/>{v.location}</p>}
          {v.bio && <p className="text-sm mt-3 leading-relaxed italic" style={{ color: '#b0a898' }}>{v.bio}</p>}
        </div>
        {/* Gold divider */}
        <div className="flex items-center gap-3"><div className="flex-1 h-px" style={{ background: `linear-gradient(to right, transparent, ${gold}60)` }}/><span className="text-[10px] tracking-[0.4em] uppercase" style={{ color: `${gold}80` }}>Contact</span><div className="flex-1 h-px" style={{ background: `linear-gradient(to left, transparent, ${gold}60)` }}/></div>
        {/* Contact */}
        <div className="space-y-2">
          {[
            [buildContactUrl('whatsapp', v.whatsapp||v.phone), <WAIcon s={15}/>, v.whatsapp||v.phone, '#25D366'],
            [buildContactUrl('phone', v.phone||v.whatsapp), <PhoneIcon s={14}/>, v.phone||v.whatsapp, gold],
            [buildContactUrl('email', v.email), <MailIcon s={14}/>, v.email, '#ef4444'],
            [v.website, <GlobeIcon s={14}/>, v.website, '#64748b'],
          ].filter(([u,,val])=>u&&val).map(([url, icon, label, color]) => (
            <a key={label} href={url} target={url?.startsWith('http')?'_blank':undefined} rel="noreferrer"
               className="flex items-center gap-3 rounded-xl px-4 py-3 border border-white/5 hover:border-white/10 transition-colors" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ color, background: `${color}20` }}>{icon}</span>
              <span className="text-sm truncate" style={{ color: '#d0c8b8' }}>{label}</span>
            </a>
          ))}
        </div>
        {/* Social */}
        <div className="flex justify-center gap-3 flex-wrap">
          {[['Facebook',social.facebook||v?.facebook],['Instagram',social.instagram||v?.instagram],['TikTok',social.tiktok||v?.tiktok],['YouTube',social.youtube||v?.youtube],['X',social.x||v?.x_twitter],['LinkedIn',social.linkedin||v?.linkedin]]
            .filter(([,u])=>u).map(([label, url]) => {
              const Icon = SI[label];
              return <a key={label} href={url} target="_blank" rel="noreferrer" title={label}
                className="w-10 h-10 rounded-full flex items-center justify-center border hover:opacity-80 transition-opacity" style={{ borderColor: `${gold}40`, color: gold, background: `${gold}10` }}>{Icon && Icon(16)}</a>;
            })}
        </div>
        {/* CTA */}
        {v.payment_link_id && <a href={v.payment_link_id} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold w-full border" style={{ borderColor: gold, color: gold, background: `${gold}10` }}><HeartIcon s={14}/>Pay / Donate</a>}
        {/* Links */}
        <CustomLinks links={links} accentColor={gold} cardBg="rgba(255,255,255,0.04)" textColor="#d0c8b8" />
        {/* Share row */}
        <div className="flex items-center gap-2 pt-2">
          {v.download_contact_enabled && <button onClick={onDownload} className="flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-xl border" style={{ borderColor: `${gold}30`, color: gold }}><DlIcon s={12}/>Save</button>}
          <button onClick={onCopy} className="flex-1 text-xs font-bold py-2 rounded-xl border" style={{ borderColor: `${gold}30`, color: `${gold}aa` }}>{copied?'✓ Copied':'Copy Link'}</button>
          <button onClick={onShare} className="flex-1 text-xs font-bold py-2 rounded-xl" style={{ background: gold, color: '#0d0d0d' }}>Share</button>
        </div>
        {v.show_jjuma_branding !== false && <p className="text-center text-xs" style={{ color: `${gold}50` }}>Powered by JJuma Pay</p>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TEMPLATE 6: CREATIVE  (vibrant gradient bg, floating glassmorphism card)
══════════════════════════════════════════════════════════════ */
function TplCreative({ v, cfg, actions, links }) {
  const { accent, font, publicUrl, copied } = cfg;
  const { onCopy, onShare, onDownload } = actions;
  const social = v.social_links || {};
  const bg = v.bg_value && v.bg_type === 'image' ? undefined : `linear-gradient(135deg, ${accent} 0%, ${accent}aa 50%, #7c3aed 100%)`;
  return (
    <div className="min-h-screen relative" style={{ background: v.cover_image ? undefined : bg, fontFamily: font }}>
      {v.cover_image && <div className="absolute inset-0 z-0"><img src={v.cover_image} alt="" className="w-full h-full object-cover"/><div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accent}cc, #7c3aed99)` }}/></div>}
      <div className="relative z-10 max-w-sm mx-auto px-4 py-8 space-y-4">
        {/* Glass card */}
        <div className="rounded-3xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.25)', boxShadow: '0 25px 50px rgba(0,0,0,0.3)' }}>
          {/* Profile */}
          <div className="p-6 text-center">
            <div className="w-28 h-28 mx-auto rounded-full overflow-hidden border-4 border-white/40 shadow-2xl mb-3">
              {v.profile_image
                ? <img src={v.profile_image} alt="" className="h-full w-full object-cover"/>
                : <div className="h-full w-full flex items-center justify-center text-4xl font-bold text-white" style={{ background: 'rgba(255,255,255,0.2)' }}>{(v.display_name||v.full_name||'J').charAt(0)}</div>}
            </div>
            <h1 className="text-2xl font-bold text-white drop-shadow">{v.display_name || v.full_name}</h1>
            {(v.brand_name || v.job_title) && <p className="text-sm font-semibold text-white/80 mt-0.5">{v.job_title || v.brand_name}</p>}
            {v.location && <p className="text-xs text-white/60 flex items-center justify-center gap-1 mt-1"><MapPinIcon s={11}/>{v.location}</p>}
            {v.bio && <p className="text-sm text-white/80 mt-2 leading-relaxed">{v.bio}</p>}
          </div>
          {/* Buttons */}
          <div className="px-5 pb-5 grid grid-cols-2 gap-2">
            {[
              [buildContactUrl('whatsapp',v.whatsapp||v.phone), <WAIcon s={14}/>, 'WhatsApp', '#25D366'],
              [buildContactUrl('phone',v.phone||v.whatsapp), <PhoneIcon s={13}/>, 'Call', accent],
              [buildContactUrl('email',v.email), <MailIcon s={13}/>, 'Email', '#ef4444'],
              [v.website, <GlobeIcon s={13}/>, 'Website', 'rgba(255,255,255,0.3)'],
            ].filter(([u,,, ])=>u).map(([url,icon,label,bg])=>(
              <a key={label} href={url} target={url?.startsWith('http')?'_blank':undefined} rel="noreferrer"
                 className="flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-bold text-white border border-white/20 hover:opacity-90 transition-opacity"
                 style={{ background: bg }}>{icon}{label}</a>
            ))}
          </div>
        </div>
        {/* Social */}
        <div className="flex justify-center gap-3 flex-wrap">
          {[['Facebook',social.facebook||v?.facebook],['Instagram',social.instagram||v?.instagram],['TikTok',social.tiktok||v?.tiktok],['YouTube',social.youtube||v?.youtube],['X',social.x||v?.x_twitter],['LinkedIn',social.linkedin||v?.linkedin]]
            .filter(([,u])=>u).map(([label,url])=>{
              const Icon = SI[label]; const c = SOCIAL_BRAND[label];
              return <a key={label} href={url} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-2xl flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform" style={{ background: c }}>{Icon&&Icon(18)}</a>;
            })}
        </div>
        {/* CTA + Links */}
        {v.payment_link_id && <a href={v.payment_link_id} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold text-white w-full shadow-xl border border-white/20" style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)' }}><HeartIcon s={14}/>Pay / Donate</a>}
        {links.length > 0 && <CustomLinks links={links} accentColor={accent} cardBg="rgba(255,255,255,0.12)" textColor="#fff"/>}
        {/* Share */}
        <div className="rounded-2xl p-3 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}>
          <p className="flex-1 text-xs text-white/60 truncate">{publicUrl}</p>
          {v.download_contact_enabled && <button onClick={onDownload} className="text-white/70 hover:text-white"><DlIcon s={14}/></button>}
          <button onClick={onCopy} className="text-xs font-bold px-2.5 py-1.5 rounded-xl text-white/80 border border-white/20">{copied?'✓':'Copy'}</button>
          <button onClick={onShare} className="text-xs font-bold px-3 py-1.5 rounded-xl text-white" style={{ background: accent }}>Share</button>
        </div>
        {v.show_jjuma_branding !== false && <p className="text-center text-xs text-white/40">Powered by JJuma Pay</p>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TEMPLATE 7: NATURE  (earthy, warm, soft organic feel)
══════════════════════════════════════════════════════════════ */
function TplNature({ v, cfg, actions, links }) {
  const { accent, font, publicUrl, copied } = cfg;
  const { onCopy, onShare, onDownload } = actions;
  const earth = accent === '#047857' ? '#5c7a3e' : accent;
  const warm  = '#fdf6ed';
  const social = v.social_links || {};
  return (
    <div className="min-h-screen py-8 px-4" style={{ background: v.cover_image ? undefined : warm, fontFamily: font }}>
      {v.cover_image && <div className="fixed inset-0 z-0 opacity-20"><img src={v.cover_image} alt="" className="w-full h-full object-cover"/></div>}
      <div className="relative z-10 max-w-sm mx-auto space-y-4">
        {/* Organic card */}
        <div className="rounded-[32px] overflow-hidden shadow-xl" style={{ background: '#fff8f0', border: `2px solid ${earth}30` }}>
          {/* Soft header */}
          <div className="h-28 relative flex items-end justify-center pb-0" style={{ background: `linear-gradient(160deg, ${earth}30, ${earth}15)` }}>
            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-xl translate-y-12" style={{ borderColor: warm }}>
              {v.profile_image
                ? <img src={v.profile_image} alt="" className="h-full w-full object-cover"/>
                : <div className="h-full w-full flex items-center justify-center text-3xl font-bold" style={{ color: earth, background: `${earth}20` }}>{(v.display_name||v.full_name||'J').charAt(0)}</div>}
            </div>
          </div>
          <div className="px-6 pt-14 pb-6 text-center">
            <h1 className="text-xl font-bold" style={{ color: '#2d2015' }}>{v.display_name || v.full_name}</h1>
            {(v.brand_name || v.job_title) && <p className="text-xs font-semibold mt-0.5" style={{ color: earth }}>{v.job_title || v.brand_name}</p>}
            {v.location && <p className="text-xs flex items-center justify-center gap-1 mt-1" style={{ color: '#8a7060' }}><MapPinIcon s={11}/>{v.location}</p>}
            {v.bio && <p className="text-sm mt-3 leading-relaxed" style={{ color: '#5a4a3a' }}>{v.bio}</p>}
            {/* Organic buttons */}
            <div className="mt-4 grid grid-cols-2 gap-2">
              {[
                [buildContactUrl('whatsapp',v.whatsapp||v.phone),<WAIcon s={14}/>,'WhatsApp','#25D366'],
                [buildContactUrl('phone',v.phone||v.whatsapp),<PhoneIcon s={13}/>,'Call',earth],
                [buildContactUrl('email',v.email),<MailIcon s={13}/>,'Email','#c2410c'],
                [v.website,<GlobeIcon s={13}/>,'Website','#6b7280'],
              ].filter(([u])=>u).map(([url,icon,label,bg])=>(
                <a key={label} href={url} target={url?.startsWith('http')?'_blank':undefined} rel="noreferrer"
                   className="flex items-center justify-center gap-1.5 rounded-2xl py-2.5 text-xs font-bold text-white"
                   style={{ background: bg }}>{icon}{label}</a>
              ))}
            </div>
            {/* Social */}
            <div className="mt-4 flex justify-center gap-2 flex-wrap">
              {[['Facebook',social.facebook||v?.facebook],['Instagram',social.instagram||v?.instagram],['TikTok',social.tiktok||v?.tiktok],['YouTube',social.youtube||v?.youtube],['X',social.x||v?.x_twitter],['LinkedIn',social.linkedin||v?.linkedin]]
                .filter(([,u])=>u).map(([label,url])=>{
                  const Icon = SI[label]; const c = SOCIAL_BRAND[label];
                  return <a key={label} href={url} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: c }}>{Icon&&Icon(14)}</a>;
                })}
            </div>
            {v.payment_link_id && <a href={v.payment_link_id} target="_blank" rel="noreferrer" className="mt-4 flex items-center justify-center gap-2 rounded-2xl py-2.5 text-sm font-bold text-white w-full" style={{ background: earth }}><HeartIcon s={14}/>Pay / Donate</a>}
          </div>
        </div>
        {links.length > 0 && <CustomLinks links={links} accentColor={earth} cardBg="#fff8f0" textColor="#2d2015"/>}
        <div className="flex gap-2">
          {v.download_contact_enabled && <button onClick={onDownload} className="flex items-center gap-1 text-xs font-bold px-3 py-2.5 rounded-2xl border" style={{ borderColor: `${earth}30`, color: earth }}><DlIcon s={12}/>Save</button>}
          <button onClick={onCopy} className="flex-1 text-xs font-bold py-2.5 rounded-2xl border" style={{ borderColor: `${earth}30`, color: earth }}>{copied?'✓ Copied':'Copy Link'}</button>
          <button onClick={onShare} className="flex-1 text-xs font-bold py-2.5 rounded-2xl text-white" style={{ background: earth }}>Share</button>
        </div>
        {v.show_jjuma_branding !== false && <p className="text-center text-xs" style={{ color: `${earth}80` }}>Powered by JJuma Pay</p>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TEMPLATE 8: CORPORATE  (clean, professional, sidebar accent)
══════════════════════════════════════════════════════════════ */
function TplCorporate({ v, cfg, actions, links }) {
  const { accent, font, publicUrl, copied } = cfg;
  const { onCopy, onShare, onDownload } = actions;
  const social = v.social_links || {};
  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" style={{ fontFamily: font }}>
      <div className="max-w-sm mx-auto space-y-3">
        <div className="bg-white rounded-3xl overflow-hidden shadow-lg">
          {/* Cover bar */}
          <div className="h-20 relative" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}99)` }}>
            {v.cover_image && <img src={v.cover_image} alt="" className="h-full w-full object-cover opacity-30"/>}
            {/* Company name overlay */}
            <div className="absolute bottom-3 left-20 text-white/80 text-xs font-bold uppercase tracking-widest">{v.brand_name || ''}</div>
          </div>
          {/* Avatar left-aligned below bar */}
          <div className="px-5 -mt-10 mb-4">
            <div className="w-20 h-20 rounded-2xl overflow-hidden border-4 border-white shadow-xl">
              {v.profile_image
                ? <img src={v.profile_image} alt="" className="h-full w-full object-cover"/>
                : <div className="h-full w-full flex items-center justify-center text-3xl font-black text-white" style={{ background: accent }}>{(v.display_name||v.full_name||'J').charAt(0)}</div>}
            </div>
          </div>
          <div className="px-5 pb-5">
            <h1 className="text-xl font-bold text-slate-900">{v.display_name || v.full_name}</h1>
            {v.job_title && <p className="text-sm text-slate-500 font-medium mt-0.5">{v.job_title}</p>}
            {v.location && <p className="text-xs text-slate-400 flex items-center gap-1 mt-1"><MapPinIcon s={10}/>{v.location}</p>}
            {v.bio && <p className="text-sm text-slate-600 mt-2 leading-relaxed border-t border-slate-50 pt-2">{v.bio}</p>}
            {/* Contact grid */}
            <div className="mt-4 grid grid-cols-2 gap-2">
              {[
                [buildContactUrl('whatsapp',v.whatsapp||v.phone),<WAIcon s={13}/>,'WhatsApp','#25D366'],
                [buildContactUrl('phone',v.phone||v.whatsapp),<PhoneIcon s={12}/>,'Call',accent],
                [buildContactUrl('email',v.email),<MailIcon s={12}/>,'Email','#dc2626'],
                [v.website,<GlobeIcon s={12}/>,'Website','#475569'],
              ].filter(([u])=>u).map(([url,icon,label,bg])=>(
                <a key={label} href={url} target={url?.startsWith('http')?'_blank':undefined} rel="noreferrer"
                   className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold text-white" style={{ background: bg }}>
                  {icon}{label}
                </a>
              ))}
            </div>
            {/* Social */}
            <div className="mt-3 flex flex-wrap gap-2">
              {[['Facebook',social.facebook||v?.facebook],['Instagram',social.instagram||v?.instagram],['TikTok',social.tiktok||v?.tiktok],['YouTube',social.youtube||v?.youtube],['X',social.x||v?.x_twitter],['LinkedIn',social.linkedin||v?.linkedin]]
                .filter(([,u])=>u).map(([label,url])=>{
                  const Icon = SI[label]; const c = SOCIAL_BRAND[label];
                  return <a key={label} href={url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: c }}>{Icon&&Icon(13)}</a>;
                })}
            </div>
            {v.payment_link_id && <a href={v.payment_link_id} target="_blank" rel="noreferrer" className="mt-3 flex items-center justify-center gap-2 rounded-2xl py-2.5 text-sm font-bold text-white w-full" style={{ background: accent }}><HeartIcon s={13}/>Pay / Donate</a>}
            {links.length > 0 && <div className="mt-3"><CustomLinks links={links} accentColor={accent} cardBg="#f8fafc"/></div>}
            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
              <p className="text-xs text-slate-400 truncate">{publicUrl}</p>
              <div className="flex gap-1 shrink-0">
                {v.download_contact_enabled && <button onClick={onDownload} className="text-xs px-2 py-1 rounded-lg border border-slate-200 text-slate-500"><DlIcon s={11}/></button>}
                <button onClick={onCopy} className="text-xs px-2 py-1 rounded-lg border border-slate-200 text-slate-600">{copied?'✓':'Copy'}</button>
                <button onClick={onShare} className="text-xs px-3 py-1 rounded-lg text-white font-bold" style={{ background: accent }}>Share</button>
              </div>
            </div>
          </div>
        </div>
        {v.show_jjuma_branding !== false && <p className="text-center text-xs text-slate-400">Powered by <span className="font-bold" style={{ color: accent }}>JJuma Pay</span></p>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   TEMPLATE ROUTER
══════════════════════════════════════════════════════════════ */
const TEMPLATE_COMPONENTS = {
  modern:    TplModern,
  musician:  TplMusician,
  minimal:   TplMinimal,
  bold:      TplBold,
  luxury:    TplLuxury,
  creative:  TplCreative,
  nature:    TplNature,
  corporate: TplCorporate,
};

/* ══════════════════════════════════════════════════════════════
   MAIN EXPORT — VCardPublic
══════════════════════════════════════════════════════════════ */
export default function VCardPublic(props = {}) {
  const params   = useParams();
  const username = props.resolvedUsername || params.username || params.slug || '';
  const [data,     setData]     = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [copied,   setCopied]   = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/vcards/${username}`)
      .then(r => setData(r.data))
      .catch(() => setNotFound(true));
  }, [username]);

  /* SEO meta */
  useEffect(() => {
    if (!data?.vcard) return;
    const v = data.vcard;
    const title       = v.seo_title || v.display_name || v.full_name || `${username} | Jjuma Link`;
    const description = v.seo_description || v.bio || '';
    const image       = v.social_share_image || v.social_sharing_image_url || v.cover_image || v.cover_image_url || v.profile_image || v.profile_photo_url || '';
    const canonical   = username ? `https://link.jjuma.com/${username}` : '';
    document.title = title;
    ensureMeta('description', description);
    ensureMeta('keywords',    Array.isArray(v.seo_keywords) ? v.seo_keywords.join(', ') : (v.seo_keywords || ''));
    ensureMeta('og:title',    title,        'property');
    ensureMeta('og:description', description,'property');
    ensureMeta('og:image',    image,        'property');
    ensureMeta('og:type',     'profile',    'property');
    ensureMeta('og:url',      canonical,    'property');
    ensureMeta('og:site_name','Jjuma Pay',  'property');
    ensureMeta('og:image:alt', v.display_name || v.full_name || title, 'property');
    ensureMeta('twitter:card',        image ? 'summary_large_image' : 'summary');
    ensureMeta('twitter:title',       title);
    ensureMeta('twitter:description', description);
    ensureMeta('twitter:image',       image);
    ensureMeta('twitter:url',         canonical);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
    link.setAttribute('href', canonical);
  }, [data, username]);

  const v     = data?.vcard;
  const links = useMemo(() => normalizeLinks(v?.links || v?.custom_links || []), [v]);

  const publicUrl = username ? `https://link.jjuma.com/${username}` : '';
  const paymentLink = v?.payment_link_id || v?.payment_link || '';
  const donationUrl = paymentLink
    ? (String(paymentLink).startsWith('http') ? paymentLink : getPayUrl(String(paymentLink).startsWith('/') ? paymentLink : `/pay/${String(paymentLink).replace(/^\/+/, '')}`))
    : '';

  const copyLink = async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  };
  const shareLink = async () => {
    if (!publicUrl) return;
    const shareData = { title: v?.seo_title || v?.display_name || v?.full_name || 'Jjuma Link', text: v?.seo_description || v?.bio || '', url: publicUrl };
    if (navigator.share) { try { await navigator.share(shareData); return; } catch {} }
    await copyLink();
  };
  const downloadContact = () => {
    if (!v) return;
    const lines = ['BEGIN:VCARD','VERSION:3.0',`FN:${v.display_name||v.full_name||''}`,v.phone?`TEL;TYPE=CELL:${v.phone}`:'',v.whatsapp?`TEL;TYPE=WHATSAPP:${v.whatsapp}`:'',v.email?`EMAIL:${v.email}`:'',v.website?`URL:${v.website}`:'',v.location?`ADR:${v.location}`:'','END:VCARD'].filter(Boolean);
    const blob = new Blob([lines.join('\n')], { type: 'text/vcard;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${(v.username||v.display_name||v.full_name||'contact').toLowerCase().replace(/[^a-z0-9]+/g,'-')}.vcf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  /* Loading / 404 */
  if (notFound || (data && !v)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white border border-slate-200 rounded-3xl shadow-lg p-10 text-center max-w-sm w-full">
          <div className="text-5xl mb-4">🔍</div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Profile Not Found</h2>
          <p className="text-sm text-slate-500">This link page doesn't exist or has been deactivated.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-[3px] border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  /* Build config object passed to every template */
  const accentKey = v.theme_color || v.design?.theme_color || 'green';
  const accent    = THEME_ACCENT[accentKey] || THEME_ACCENT.green;
  const fontKey   = v.font_family || v.design?.font_family || 'outfit';
  const font      = FONT_MAP[fontKey] || FONT_MAP.outfit;
  const bgType    = v.bg_type || v.design?.bg_type || 'gradient';
  const bgValue   = v.bg_value || v.design?.bg_value || '';
  let   bg;
  if (bgType === 'image' && bgValue) {
    bg = undefined; // handled by cover_image in each template or via inline style
    // inject bg image on body
  } else if (bgType === 'solid' && bgValue) {
    bg = bgValue;
  } else {
    bg = `linear-gradient(160deg, ${accent}22 0%, #f1f5f9 100%)`;
  }

  const templateKey = v.template || v.design?.template || 'modern';
  const TemplateComp = TEMPLATE_COMPONENTS[templateKey] || TplModern;

  // Override vcard payment link with resolved url
  const vWithPayment = { ...v, payment_link_id: donationUrl || v.payment_link_id };

  const cfg = { accent, font, bg, publicUrl, copied };
  const actions = { onCopy: copyLink, onShare: shareLink, onDownload: downloadContact };

  return <TemplateComp v={vWithPayment} cfg={cfg} actions={actions} links={links} />;
}
